import { createSlice } from '@reduxjs/toolkit'
import { convertConfigFiltersToNormalFormat, getSpotIndex } from '../../Trainer/utils/helpers'
import { countHands, drawModalMatrix } from '../../Hands/utils'
import { mockEquity } from '../../Hands/mockEquity'

const boardFilters = {
	Flop: {
		filterMode: 'All',
		suits: {
			rainbow: null,
			twotone: null,
			monotone: null,
		},
		cards: {
			high: null,
			mid: null,
			low: null,
		},
		ranks: {
			paired: null,
			unpaired: null,
			trips: null,
		},
		card_ranks: {
			high_rank: ['Any'],
			middle_rank: ['Any'],
			low_rank: ['Any'],
		},
		subset: ['Any'],
		texture: [],
	},
	Turn: {
		filterMode: 'All',
		suits: {
			rainbow: null,
			twotone: null,
			monotone: null,
		},
		cards: {
			high: null,
			mid: null,
			low: null,
		},
		ranks: {
			paired: null,
			unpaired: null,
			trips: null,
		},
		card_ranks: {
			high_rank: ['Any'],
		},
		texture: [],
	},
	River: {
		filterMode: 'All',
		suits: {
			rainbow: null,
			twotone: null,
			monotone: null,
		},
		cards: {
			high: null,
			mid: null,
			low: null,
		},
		ranks: {
			paired: null,
			unpaired: null,
			trips: null,
		},
		card_ranks: {
			high_rank: ['Any'],
		},
		texture: [],
	},
	Connectedness: {
		disconnected: null,
		medium: null,
		connected: null,
	},
}

const boardFiltersMode = {
	Flop: 'All',
	Turn: 'All',
	River: 'All',
}

const initConfigDescription = {
	stack: [],
	hero_positions: [],
	hero_oop_ip: null,
	hero_preflop_action: null,
	hero_action: null,
	villain_positions: [],
	gameMode: null,
	board: ['-', '-', '-', '-', '-'],
	query: null,
	boardFilters: null,
	Connectedness: {},
	Flop: {},
	Turn: {},
	River: {},
	enablePositions: [],
	enableMultiEffStacks: [],
	handsCountByFilter: null,
	equityThreshold: [0, 100],
	evThreshold: [0, 100],
}

const initialState = {
	isEditQueryDialogOpen: false,
	isFetchingQueryData: false,
	editQueryDialogData: null,
	isLoadingConfigurationStats: false,
	playersArr: {},
	isLoadingConfigName: false,
	isLoadingUpdateConfig: false,
	updateNameBorderErr: false,
	savingConfigurationName: 'New configuration',
	settingsModalBtn: 'start',
	savedHandId: null,
	/* For multiple heroes */
	heroPositions: [],
	enablePositions: [],
	villainPositions: [],
	enableVillainPositions: [],
	multiEffStacks: [],
	enableMultiEffStacks: [],
	allEffStacks: [],
	configurationId: null,
	trainerConfigurationStats: [],
	isCreateDrillModal: false,
	isShowEquityLinear: false, // deprecated
	currentConfigOffset: 0,
	currentConfigLimit: null,
	currentSessionOffset: null,
	currentSessionLimit: null,
	isLoadingSessionConfig: false,
	totalPresets: null,
	totalSessions: null,
	unfilteredTotalPresets: null,
	allPlayers: null,
	isStepFromViewPanel: false,
	checkStepFrom: 'trainer',
	queryFromViewPanel: '',
	spotNameFromViewPanel: '',
	maxStack: 15,
	minStack: 5,
	heroActivePlayer: ['', '', '', ''],
	villainActivePlayer: [null, null, null, null],
	villainNoActivePlayer: [null, null, null, null],
	viewRangeOption: 'abs',
	viewRangeOptionHero: ['abs', 'abs', 'abs', 'abs'],
	viewRangeOptionVillain: ['abs', 'abs', 'abs', 'abs'],
	board: ['-', '-', '-', '-', '-'],
	modalVisible: false,
	effStack: null,
	isLoadingSessionHistory: false,
	selectedPresetId: null,
	selectedSessionId: null,
	sessionFilters: {
		library: null,
		spotId: null,
		name: null,
		is_manual: true,
	},
	sessionHistoryData: [],
	isDrillModalVisible: false,
	configurationsData: [],
	isReady: false,
	isLoadingConfig: false,
	isShowAdvancedModal: false,
	currentSettingsPreset: null,
	isModifiedPreset: false,
	localSettingsPreset: {
		board: ['-', '-', '-', '-', '-'],
		filterMode: { ...boardFiltersMode },
		gameLibrary: '6-Max Cash',
		players: null,
		settings: { ...boardFilters },
		spotId: null,
		spotName: '',
		startQuery: '',
	},
	editableDrill: null,
	filterMode: { ...boardFiltersMode },
	gameLibrary: '6-Max Cash',
	gameMode: 'hand',
	spotName: '',
	spotId: null,
	playersMode: 'RANDOM',
	startQuery: '',
	isUseSound: false,
	isShowHeroRange: true,
	isShowVillainRange: true,
	isShowHeroCombos: false, // deprecated
	isShowHeroHandStrength: false, // deprecated
	isShowHeroComboDraw: false, // deprecated
	isShowVillainStrategy: true,
	isShowHeroEquity: true,
	isDrillMode: true,
	isFullScreenMode: false,
	isShowHotkeys: false,
	isErrorDetailsModalShown: 'on',
	isErrorDialogVisible: false,
	isTrainerSettingsModalVisible: false,
	isTrainerBottomTableHidden: false,
	filters: { ...boardFilters },
	filterBoard: [],
	isStartLinesLoading: false,
	isStartTraining: false,
	heroOopIp: null,
	configMode: 'handStart',
	heroAction: null,
	heroPreflopAction: null,
	isNewConfigVersion: false,
	selectedStreetAction: null, // preflop: RFI, vs Open, vs 3-Bet and etc. or postlop: SRP, 3-Bet, 4-Bet and etc.
	defaultStreetAction: null,
	isHelp: false,
	isShowTooltip: true,
	crossFilters: null,
	spotLines: null,
	customTrace: null,
	customStacks: null,
	traceStatusLoading: 'success',
	isEvaluateShow: false,
	configDescription: {
		...initConfigDescription,
	},
	isCollapsedSidebars: { left: false, right: false },
	filterByHands: null,
	handsRange: [0, 100],
	handsCountByFilter: null,
	equityThreshold: [0, 100],
	evThreshold: [0, 100],
	configModalTab: 'game',
}

const trainerSettings = createSlice({
	initialState,
	name: 'trainerSettings',
	reducers: {
		setTrainerBottomTableHiddenState(state) {
			state.isTrainerBottomTableHidden = !state.isTrainerBottomTableHidden
		},
		setConfigModalTab(state, action) {
			state.configModalTab = action.payload
		},
		setFilterByHands(state, action) {
			state.filterByHands = action.payload
		},
		setEquityThreshold(state, action) {
			state.equityThreshold = action.payload
		},
		setEVThreshold(state, action) {
			state.evThreshold = action.payload
		},
		setHandRange(state, action) {
			state.handsRange = action.payload
		},
		setHandsCountByFilter(state, action) {
			state.handsCountByFilter = action.payload
		},
		setSpotLines(state, action) {
			state.spotLines = action.payload
		},
		setCustomTrace(state, action) {
			state.customTrace = action.payload.trace
			state.customStacks = action.payload.stacks
		},
		setCustomTraceLoadingStatus(state, action) {
			state.traceStatusLoading = action.payload
		},

		setTrainerDialogSettingsVisibility(state, action) {
			state.isTrainerSettingsModalVisible = action.payload
		},

		setIsLoadingConfig(state, action) {
			state.isLoadingConfig = action.payload
		},

		setCrossFilters(state, action) {
			state.crossFilters = action.payload
		},

		setIsLoadingConfigName(state, action) {
			state.isLoadingConfigName = action.payload
		},

		setIsLoadingConfigurationStats(state, action) {
			state.isLoadingConfigurationStats = action.payload
		},

		setConfigurationId(state, action) {
			state.configurationId = action.payload
		},

		setSettingsModalBtn(state, action) {
			state.settingsModalBtn = action.payload
		},

		setTrainerConfigurationStats(state, action) {
			state.trainerConfigurationStats = action.payload
		},

		setCurrentConfigurationOffset(state, action) {
			state.currentConfigOffset = action.payload
		},
		setCurrentConfigurationLimit(state, action) {
			state.currentConfigLimit = action.payload
		},

		setCurrentSessionOffset(state, action) {
			state.currentSessionOffset = action.payload
		},
		setCurrentSessionLimit(state, action) {
			state.currentSessionLimit = action.payload
		},

		setIsLoadingSessionHistory(state, action) {
			state.isLoadingSessionHistory = action.payload
		},
		setIsCreateDrillModal(state, action) {
			state.isCreateDrillModal = action.payload
		},

		setTrainerGameMode(state, action) {
			const { gameMode } = action.payload
			state.gameMode = gameMode
		},
		setIsLoadingSessionConfig(state, action) {
			state.isLoadingSessionConfig = action.payload
		},
		setIsLoadingUpdateConfig(state, action) {
			state.isLoadingUpdateConfig = action.payload
		},

		setIsReady(state, action) {
			state.isReady = action.payload
		},

		setEditableDrill(state, action) {
			state.editableDrill = action.payload
		},

		setUpdateNameBorderErr(state, action) {
			state.updateNameBorderErr = action.payload
		},

		setHeroPositions(state, action) {
			state.heroPositions = action.payload
		},
		setMultiEffStacks(state, action) {
			state.multiEffStacks = action.payload
		},

		setSessionFilters(state, action) {
			const { filterType, filterValue } = action.payload
			state.sessionFilters[filterType] = filterValue
		},

		setUpdateDrillName(state, action) {
			state.editableDrill.name = action.payload
		},

		setErrorDialogVisibilityState(state, action) {
			state.isErrorDialogVisible = action.payload
		},
		setErrorDetailsModalState(state, action) {
			state.isErrorDetailsModalShown = action.payload
		},
		setStartQuery(state, action) {
			state.startQuery = action.payload
		},
		setEditQueryDialogQueryLoading(state, action) {
			state.isFetchingQueryData = action.payload
		},
		setToggleFullScreenMode(state, action) {
			state.isFullScreenMode = action.payload
		},
		setIsUseSound(state, action) {
			state.isUseSound = action.payload
		},

		setLocalPresetFullData(state, action) {
			state.localSettingsPreset = action.payload
		},
		setResetSettingsFilter(state) {
			Object.assign(state.filters, initialState.filters)
			Object.assign(state.board, initialState.board)
			Object.assign(state.filterMode, initialState.filterMode)
		},

		setEditQueryDialogData(state, action) {
			state.editQueryDialogData = action.payload
			state.editQueryDialogData.title = 'ROOT'
			state.editQueryDialogData.key = 'ROOT'
		},
		setViewRangeOption(state, action) {
			state.viewRangeOption = action.payload
		},
		setViewRangeOptionHero(state, action) {
			const { index, data } = action.payload
			state.viewRangeOptionHero[index] = data
		},
		setViewRangeOptionVillain(state, action) {
			const { index, data } = action.payload
			state.viewRangeOptionVillain[index] = data
		},

		setIsShowAdvanceModal(state, action) {
			state.isShowAdvancedModal = action.payload
		},

		setLocalSettingsPreset(state, action) {
			const { type, value } = action.payload
			state.localSettingsPreset[type] = value
		},

		setQueryFromViewPanel(state, action) {
			state.queryFromViewPanel = action.payload
			localStorage.setItem('viewStartQuery', action.payload)
		},
		resetQuery(state) {
			state.startQuery = state.effStack || ''
		},
		setCheckStepFrom(state, action) {
			state.checkStepFrom = action.payload
		},
		setFilterBoard(state, action) {
			const { board } = action.payload

			const flop = board.slice(0, 3)
			const turn = board[3]
			const river = board[4]
			state.filters.Flop.texture = flop.every((el) => el === '-') ? [] : flop.every((el) => el !== '-') ? flop : []
			state.filters.Turn.texture = turn !== '-' ? [turn] : []
			state.filters.River.texture = river !== '-' ? [river] : []
		},

		setSpotNameFromViewPanel(state, action) {
			state.spotNameFromViewPanel = action.payload
		},

		setIsStepFromViewPanel(state, action) {
			state.isStepFromViewPanel = action.payload
		},

		setMinStack(state, action) {
			state.minStack = action.payload
		},
		setMaxStack(state, action) {
			state.maxStack = action.payload
		},

		setEffStack(state, action) {
			state.effStack = action.payload
		},

		setIsShowHeroRange(state, action) {
			state.isShowHeroRange = action.payload
		},
		setIsShowHeroCombos(state, action) {
			state.isShowHeroCombos = action.payload
		},
		setIsShowHeroComboDraw(state, action) {
			state.isShowHeroComboDraw = action.payload
		},

		setIsShowEquityLinear(state, action) {
			state.isShowEquityLinear = action.payload
		},

		setIsShowHeroHandStrength(state, action) {
			state.isShowHeroHandStrength = action.payload
		},

		setIsShowVillainRange(state, action) {
			state.isShowVillainRange = action.payload
		},

		setToggleVisible(state, action) {
			state.modalVisible = action.payload
		},

		setResetFilterMode(state) {
			Object.assign(state.filterMode, initialState.filterMode)

			/** Fix it **/
			state.filters['Flop'].filterMode = 'Filter'
			state.filters['Turn'].filterMode = 'Filter'
			state.filters['River'].filterMode = 'Filter'
		},

		setResetFilters(state) {
			Object.assign(state.filters, initialState.filters)
		},

		setResetTermFilters(state, action) {
			const { term, boardLocal } = action.payload
			const newState = JSON.parse(JSON.stringify(initialState.filters[term]))

			if (term === 'Flop') {
				state.filters.Connectedness = initialState.filters.Connectedness
			}

			newState.texture = boardLocal.every((el) => el !== '-') ? boardLocal : []

			state.filters[term] = newState
		},

		setResetTermTypeFilters(state, action) {
			const { term, type } = action.payload
			Object.assign(state.filters[term][type], initialState.filters[term][type])
		},

		resetConnectedness(state) {
			state.filters.Connectedness = initialState.filters.Connectedness
		},

		setSelectedPresetId(state, action) {
			state.selectedPresetId = action.payload
		},
		setSelectedSessionId(state, action) {
			state.selectedSessionId = action.payload
		},

		setBoard(state, action) {
			const { board, type } = action.payload

			if (type === 'flop') {
				state.board[0] = board[0]
				state.board[1] = board[1]
				state.board[2] = board[2]
			}
			if (type === 'turn') {
				state.board[3] = board
			}
			if (type === 'river') {
				state.board[4] = board
			}
			if (!type) {
				state.board = board
			}
		},
		setFilter(state, action) {
			const { filter, correctValue, type, term } = action.payload
			if (type) {
				state.filters[term][type][filter] = correctValue
			} else {
				state.filters[term][filter] = correctValue
			}
		},
		setFlopFilters(state, action) {
			const { term, type, vals, subType } = action.payload

			if (subType) {
				state.filters[term][subType][type] = vals
			} else {
				state.filters[term][type] = vals
			}
		},

		setBoardAllFilters(state, action) {
			const { boardFilters, boardFiltersMode, boardCards } = action.payload

			state.board = boardCards
			Object.assign(state.filters, boardFilters)
			Object.assign(state.filterMode, boardFiltersMode)
		},

		// setFilterMode(state, action) {
		// 	const { term, mode } = action.payload
		// 	state.filters[term].filterMode = mode
		// 	state.filterMode[term] = mode
		// },
		setGameLibrary(state, action) {
			state.gameLibrary = action.payload
		},

		setSpotId(state, action) {
			state.spotId = action.payload
		},

		setSpotName(state, action) {
			state.spotName = action.payload
		},

		setIsDrillModalVisible(state, action) {
			state.isDrillModalVisible = action.payload
		},

		setPlayersMode(state, action) {
			state.playersMode = action.payload
		},

		setSavingConfigurationName(state, action) {
			state.savingConfigurationName = action.payload
		},

		setHeroActivePlayer(state, action) {
			const { index, data } = action.payload
			state.heroActivePlayer[index] = data
		},
		setVillainActivePlayer(state, action) {
			const { index, data } = action.payload
			state.villainActivePlayer[index] = data
		},

		setVillainNoActivePlayer(state, action) {
			const { index, data } = action.payload
			state.villainNoActivePlayer[index] = data
		},

		setPlayersArrFromLibs(state, action) {
			state.playersArr = action.payload
		},

		setEnablePositions(state, action) {
			state.enablePositions = action.payload
		},

		setAllPositions(state, action) {
			state.allPlayers = action.payload
		},

		setIsShowVillainStrategy(state, action) {
			state.isShowVillainStrategy = action.payload
		},

		setIsShowHeroEquity(state, action) {
			state.isShowHeroEquity = action.payload
		},
		setIsShowHotkeys(state, action) {
			state.isShowHotkeys = action.payload
		},

		setConfigurationsData(state, action) {
			const { presets, total, unfilteredTotalPresets } = action.payload
			state.totalPresets = total
			state.configurationsData = presets
			state.unfilteredTotalPresets = unfilteredTotalPresets
		},

		setSessionHistoryData(state, action) {
			const { sessions, total } = action.payload
			state.totalSessions = total
			state.sessionHistoryData = sessions
		},

		setSavedHandId(state, action) {
			state.savedHandId = action.payload
		},

		setSavedConfigurationToStartTrain(state, action) {
			const { settings, allPlayers, gameLibrary, spotName, isStart } = action.payload

			const {
				customQuery,
				hero_positions,
				spotId,
				type,
				name,
				gameMode,
				playersMode,
				stack,
				villain_positions,
				hero_action,
				hero_oop_ip,
				hero_preflop_action,
				start_hands,
				equity_threshold,
				ev_threshold,
			} = settings

			const flop = settings['Flop'].texture.length > 1 ? settings['Flop'].texture : ['-', '-', '-']
			const turn = settings['Turn'].texture.length > 0 ? settings['Turn'].texture : ['-']
			const river = settings['River'].texture.length > 0 ? settings['River'].texture : ['-']

			state.filters = {
				Connectedness: settings['Connectedness'],
			}

			state.filters.Flop = {
				...settings['Flop'],
				card_ranks: {
					high_rank: settings['Flop'].card_ranks.high_rank ?? ['Any'],
					middle_rank: settings['Flop'].card_ranks.middle_rank ?? ['Any'],
					low_rank: settings['Flop'].card_ranks.low_rank ?? ['Any'],
				},
				subset: !settings['Flop'].subset ? ['Any'] : [settings['Flop'].subset],
			}

			state.filters.Turn = {
				...settings['Turn'],
				card_ranks: {
					high_rank: settings['Turn'].card_ranks.high_rank ?? ['Any'],
				},
			}

			state.filters.River = {
				...settings['River'],
				card_ranks: {
					high_rank: settings['River'].card_ranks.high_rank ?? ['Any'],
				},
			}

			state.gameMode = gameMode
			state.heroPositions = hero_positions ?? []
			state.multiEffStacks = stack ?? []
			state.configMode = type[0]
			state.gameLibrary = gameLibrary
			state.startQuery = customQuery ?? ''
			state.spotId = spotId
			state.playersMode = playersMode[0] || 'RANDOM'
			state.spotName = spotName
			state.board = flop.concat(turn).concat(river)
			state.allPlayers = allPlayers
			state.selectedStreetAction = name?.[0] || null
			state.filterByHands = start_hands

			state.equityThreshold = equity_threshold ?? [0, 100]
			state.evThreshold = ev_threshold ?? [0, 100]

			if (start_hands) {
				const matrixData = drawModalMatrix(start_hands, mockEquity)

				state.handsCountByFilter = countHands(matrixData)
			}

			if ((type[0] === 'preflop' || type[0] === 'postflop') && isStart) {
				state.crossFilters = convertConfigFiltersToNormalFormat({
					stack,
					hero_positions,
					villain_positions,
					hero_action,
					hero_oop_ip,
					hero_preflop_action,
				})
			}
		},
		setIsLoadingStartLines(state, action) {
			state.isStartLinesLoading = action.payload
		},

		setIsStartTraining(state, action) {
			state.isStartTraining = action.payload
		},

		setHeroOopIp(state, action) {
			state.heroOopIp = action.payload
		},

		setConfigMode(state, action) {
			state.configMode = action.payload
		},
		setVillainPositions(state, action) {
			state.villainPositions = action.payload
		},
		setEnableVillainPositions(state, action) {
			state.enableVillainPositions = action.payload
		},
		setHeroAction(state, action) {
			state.heroAction = action.payload
		},
		setHeroPreflopAction(state, action) {
			state.heroPreflopAction = action.payload
		},

		setIsNewConfigVersion(state, action) {
			state.isNewConfigVersion = action.payload
		},

		setEnableMultiEffStacks(state, action) {
			state.enableMultiEffStacks = action.payload
		},
		setSelectedStreetAction(state, action) {
			state.selectedStreetAction = action.payload
		},
		setAllEffStacks(state, action) {
			state.allEffStacks = action.payload
		},
		resetDefaultStreetAction(state) {
			state.defaultStreetAction = null
		},
		resetConfigFiltersToDefault(state) {
			state.multiEffStacks = []
			state.enableMultiEffStacks = []
			state.selectedStreetAction = null
			state.heroPositions = []
			state.enablePositions = []
		},
		setIsHelp(state, action) {
			state.isHelp = action.payload
		},
		setIsShowTooltip(state, action) {
			state.isShowTooltip = action.payload
		},
		setIsEvaluateShow(state, action) {
			state.isEvaluateShow = action.payload
		},
		setConfigDescription(state, action) {
			state.configDescription = {
				...state.configDescription,
				...action.payload,
				board: [
					action.payload?.Flop?.texture[0] || '-',
					action.payload?.Flop?.texture[1] || '-',
					action.payload?.Flop?.texture[2] || '-',
					action.payload?.Turn?.texture[0] || '-',
					action.payload?.River?.texture[0] || '-',
				],
				enablePositions: state.enablePositions,
				enableMultiEffStacks: state.enableMultiEffStacks,
				handsCountByFilter: state.handsCountByFilter,
				equityThreshold: state.equityThreshold,
				evThreshold: state.evThreshold,
			}
		},
		setResetConfigDescription(state) {
			state.configDescription = { ...initConfigDescription }
		},

		setIsCollapsedSidebars(state, action) {
			state.isCollapsedSidebars = action.payload
		},

		resetTrainerSettings() {
			return initialState
		},
	},
})

export const setIsNewTrainerConfigVersion = (action) => (dispatch) => {
	dispatch(actions.setIsNewConfigVersion(action))
}

export const setSavedHandId = (id) => (dispatch) => {
	dispatch(actions.setSavedHandId(id))
}

export const setAllPositions = (value) => (dispatch) => dispatch(actions.setAllPositions(value))

export const setConfigurationId = (id) => (dispatch) => dispatch(actions.setConfigurationId(id))

export const setIsCreateDrillModal = (value) => (dispatch) => dispatch(actions.setIsCreateDrillModal(value))

export const setHeroPositions = (positions) => (dispatch) => dispatch(actions.setHeroPositions(positions))
// to do the same as with heroPositions
export const setMultiEffStacks = (stacks) => (dispatch) => dispatch(actions.setMultiEffStacks(stacks))

export const setCurrentConfigurationOffset = (val) => (dispatch) => dispatch(actions.setCurrentConfigurationOffset(val))
export const setCurrentConfigurationLimit = (val) => (dispatch) => dispatch(actions.setCurrentConfigurationLimit(val))

export const setUpdateNameBorderErr = (isErr) => (dispatch) => dispatch(actions.setUpdateNameBorderErr(isErr))

export const setEnablePositions = (arr) => (dispatch) => dispatch(actions.setEnablePositions(arr))

export const setTrainerGameMode = (gameMode) => (dispatch) =>
	dispatch(actions.setTrainerGameMode({ gameMode: gameMode }))

export const setIsReady = (value) => (dispatch) => {
	dispatch(actions.setIsReady(value))
}

export const setEditableDrill = (value) => (dispatch) => {
	dispatch(actions.setEditableDrill(value))
}

export const setSessionFilters =
	({ filterType, filterValue }) =>
	(dispatch) => {
		dispatch(actions.setSessionFilters({ filterType, filterValue }))
	}

export const setSelectedPresetId = (presetId) => (dispatch) => {
	dispatch(actions.setSelectedPresetId(presetId))
}

export const setSelectedSessionId = (sessionId) => (dispatch) => {
	dispatch(actions.setSelectedSessionId(sessionId))
}

export const setUpdateDrillName = (name) => (dispatch) => {
	dispatch(actions.setUpdateDrillName(name))
}

export const setSettingsModalBtn = (btn) => (dispatch) => {
	dispatch(actions.setSettingsModalBtn(btn))
}

export const setBoardAllFilters =
	({ boardFilters, boardFiltersMode, boardCards }) =>
	(dispatch) => {
		dispatch(actions.setBoardAllFilters({ boardFilters, boardFiltersMode, boardCards }))
	}

export const setLocalSettingsPreset =
	({ type, value }) =>
	(dispatch) => {
		dispatch(actions.setLocalSettingsPreset({ type, value }))
	}

/* Fow advance extra modal */
export const setIsShowAdvanceModal = (value) => (dispatch) => dispatch(actions.setIsShowAdvanceModal(value))

export const setIsDrillModalVisible = (isVisible) => (dispatch) => dispatch(actions.setIsDrillModalVisible(isVisible))

export const setSavingConfigurationName = (value) => (dispatch) => dispatch(actions.setSavingConfigurationName(value))

export const setPlayersArrFromLibs = (value) => (dispatch) => dispatch(actions.setPlayersArrFromLibs(value))
export const setTrainerDialogSettingsVisibility = (state) => (dispatch) =>
	dispatch(actions.setTrainerDialogSettingsVisibility(state))
// export const setErrorDetailsModalState = (state) => (dispatch) => dispatch(actions.setErrorDetailsModalState(state))
export const setErrorDialogVisibilityState = (state) => (dispatch) =>
	dispatch(actions.setErrorDialogVisibilityState(state))
export const setTrainerBottomTableHiddenState = (state) => (dispatch) =>
	dispatch(actions.setTrainerBottomTableHiddenState(state))
export const setIsUseSound = (useSound) => (dispatch) => {
	dispatch(actions.setIsUseSound(useSound))
}
export const setToggleFullScreenMode = (mode) => (dispatch) => dispatch(actions.setToggleFullScreenMode(mode))

export const setGameLibrary = (lib, query) => (dispatch, getState) => {
	dispatch(actions.setGameLibrary(lib))
	const { librariesResponse } = getState().libs
	const spotIndex = getSpotIndex(librariesResponse, lib, true)

	// auto complete start
	const { spot_id, spot, spot_positions, all_positions } =
		librariesResponse && librariesResponse.libraries.find((el) => el.library_name === lib && el.spots).spots[spotIndex]

	localStorage.setItem('trainerSelectedSpot', spot_id)

	if (!query) {
		/* Auto write all selectors */
		dispatch(actions.setSpotName(spot))
		dispatch(setTrainerGameMode('hand'))
		dispatch(actions.setConfigMode('handStart'))
		dispatch(actions.setSpotId(spot_id))
		dispatch(actions.setStartQuery(''))
		dispatch(actions.setAllPositions(all_positions))
		dispatch(actions.setEnablePositions(spot_positions))
		dispatch(actions.setHeroPositions([]))
		dispatch(actions.setSelectedStreetAction(null))
		dispatch(actions.setPlayersMode('RANDOM'))
		dispatch(actions.setMultiEffStacks([]))
		dispatch(actions.setCustomTrace({ trace: null, stacks: null }))
		dispatch(actions.setCrossFilters(null))
	}
	// auto complete end
}

export const setViewRangeOptionHero = (value, index) => (dispatch) => {
	dispatch(actions.setViewRangeOptionHero({ data: value, index }))
}
export const setViewRangeOptionVillain = (value, index) => (dispatch) => {
	dispatch(actions.setViewRangeOptionVillain({ data: value, index }))
}

export const setBoard = (board, type) => (dispatch) => {
	dispatch(actions.setBoard({ board, type }))
}

export const setGameSpotId = (id) => (dispatch) => {
	dispatch(actions.setSpotId(id))
}

export const setStartQuerySettings = (query) => (dispatch) => {
	dispatch(actions.setStartQuery(query))
}

export const setSpotName = (value) => (dispatch) => {
	dispatch(actions.setSpotName(value))
}

export const setPlayersMode = (value) => (dispatch) => {
	dispatch(actions.setPlayersMode(value))
}

export const setEffStack = (value) => (dispatch) => {
	dispatch(actions.setEffStack(value))
}

export const setHeroActivePlayer = (actPlayer, index) => (dispatch) => {
	dispatch(actions.setHeroActivePlayer({ data: actPlayer, index }))
}
export const setVillainActivePlayer = (actPlayer, index) => (dispatch) => {
	dispatch(actions.setVillainActivePlayer({ data: actPlayer, index }))
}

export const setVillainNoActivePlayer = (value, index) => (dispatch) => {
	dispatch(actions.setVillainNoActivePlayer({ data: value, index }))
}

export const setIsShowHotkeys = (value) => (dispatch) => {
	dispatch(actions.setIsShowHotkeys(value))
}

export const setResetFilterMode = () => (dispatch) => {
	dispatch(actions.setResetFilterMode())
}

export const setFilter =
	({ filter, value, type, term }) =>
	(dispatch) => {
		const correctValue = value === false ? null : value
		dispatch(actions.setFilter({ filter, correctValue, type, term }))
	}

export const setResetFilters = () => (dispatch) => {
	dispatch(actions.setResetFilters())
}

export const setIsStepFromViewPanel = (value) => (dispatch) => {
	dispatch(actions.setIsStepFromViewPanel(value))
}

export const setCheckStepFrom = (value) => (dispatch) => {
	dispatch(actions.setCheckStepFrom(value))
}

export const setQueryFromViewPanel = (query) => (dispatch) => {
	dispatch(actions.setQueryFromViewPanel(query))
}

export const setSpotNameFromViewPanel = (spotName) => (dispatch) => {
	dispatch(actions.setSpotNameFromViewPanel(spotName))
}

export const setIsShowTooltip = (value) => (dispatch) => {
	dispatch(actions.setIsShowTooltip(value))
}

export const { reducer } = trainerSettings
export const { actions } = trainerSettings
